import { GENIUS_V2_SCHEMA, PRESTO_V2_SCHEMA } from '@/lib/supabase';

/**
 * Provider registry — the single point of extension for adding a new vendor.
 *
 * To onboard a third provider (e.g. SportRadar):
 *   1. Add its V2 schema to Supabase "Exposed schemas".
 *   2. Export a new SupabaseClient for that schema in src/lib/supabase.ts.
 *   3. Append a ProviderDefinition entry below.
 *
 * The Admin app will automatically:
 *   - render a sidebar group for the new provider,
 *   - list its sports / entities on /entities/{providerKey},
 *   - power the dynamic browser at /entities/{providerKey}/{sport}/{entityKey}.
 *
 * No other code changes required.
 */
export type ProviderKey = 'genius' | 'presto' | (string & {});

export interface EntityColumn {
  /** Column name as it exists in the Postgres table. */
  key: string;
  /** Human-readable header for the table. */
  label: string;
  /** Optional formatter (date, bytes, etc.). Defaults to `String(value)`. */
  format?: (value: unknown) => string;
  /** Render as a monospace code chip. */
  mono?: boolean;
}

export interface EntityDefinition {
  /** URL slug + cache key. */
  key: string;
  /** Display name in the UI. */
  label: string;
  /** Postgres table name within the provider schema. */
  table: string;
  /**
   * harvest.manifest `endpoint_type`(s) that feed this entity, for the
   * harvest-vs-sync chart. Defaults to the table name hyphenated
   * (e.g. `event_team_stats` → `event-team-stats`), which matches every entity
   * except those fed by differently-named or multiple endpoints (e.g. genius
   * `persons` ← league-persons + competition-team-persons + league-team-persons).
   */
  harvestEndpointTypes?: string[];
  /** Optional natural language description shown on the entity page. */
  description?: string;
  /** Column the sport filter applies against. Default: `sport_key`. */
  sportColumn?: string;
  /** Default ordering column. */
  orderBy?: { column: string; ascending: boolean };
  /** Columns to render in the entity table — first 5–8 are usually enough. */
  columns: EntityColumn[];
}

export interface ProviderDefinition {
  key: ProviderKey;
  label: string;
  /** Schema in `sports_db` that hosts this provider's V2 tables. */
  schema: string;
  /** Hangfire queue this provider's jobs run on. */
  hangfireQueue: string;
  /** Sport keys this provider currently supports (drives the sport selector). */
  sports: string[];
  /** Short blurb shown on the provider's landing page. */
  tagline: string;
  /** Entity catalog — what the Admin can browse/manage. */
  entities: EntityDefinition[];
}

const isoDate = (v: unknown) => (v ? new Date(String(v)).toLocaleString() : '—');

const HARVEST_SPORTS = [
  'basketball',
  'football',
  'soccer',
  'baseball',
  'volleyball',
  'hockey',
  'lacrosse',
  'softball',
  'tennis',
  'golf',
  'track-and-field',
  'swimming',
  'wrestling',
  'field-hockey',
  'water-polo',
];

export const PROVIDERS: ProviderDefinition[] = [
  {
    key: 'genius',
    label: 'Genius Sports',
    schema: GENIUS_V2_SCHEMA,
    hangfireQueue: 'harvest-genius',
    sports: HARVEST_SPORTS,
    tagline: 'Multi-sport NCAA data via the Genius Sports API. Backed by the genius_v2.* schema.',
    entities: [
      {
        key: 'leagues',
        label: 'Leagues',
        table: 'leagues',
        description: 'Top of the Genius hierarchy. One row per league (currently NCAA Basketball).',
        orderBy: { column: 'id', ascending: true },
        columns: [
          { key: 'id', label: 'ID', mono: true },
          { key: 'vendor_id', label: 'Vendor ID', mono: true },
          { key: 'sport_key', label: 'Sport' },
          { key: 'league_name', label: 'Name' },
          { key: 'league_country', label: 'Country' },
          { key: 'sync_watermark', label: 'Last sync run', mono: true },
        ],
      },
      {
        key: 'divisions',
        label: 'Divisions',
        table: 'divisions',
        orderBy: { column: 'id', ascending: true },
        columns: [
          { key: 'id', label: 'ID', mono: true },
          { key: 'vendor_id', label: 'Vendor ID', mono: true },
          { key: 'sport_key', label: 'Sport' },
          { key: 'league_id', label: 'League', mono: true },
          { key: 'division_name', label: 'Name' },
        ],
      },
      {
        key: 'conferences',
        label: 'Conferences',
        table: 'conferences',
        orderBy: { column: 'id', ascending: true },
        columns: [
          { key: 'id', label: 'ID', mono: true },
          { key: 'vendor_id', label: 'Vendor ID', mono: true },
          { key: 'sport_key', label: 'Sport' },
          { key: 'league_id', label: 'League', mono: true },
          { key: 'division_id', label: 'Division', mono: true },
          { key: 'conference_name', label: 'Name' },
        ],
      },
      {
        key: 'competitions',
        label: 'Competitions',
        table: 'competitions',
        orderBy: { column: 'competition_year', ascending: false },
        columns: [
          { key: 'id', label: 'ID', mono: true },
          { key: 'sport_key', label: 'Sport' },
          { key: 'competition_name', label: 'Name' },
          { key: 'competition_year', label: 'Year' },
          { key: 'competition_gender', label: 'Gender' },
          { key: 'is_active', label: 'Active' },
        ],
      },
      {
        key: 'clubs',
        label: 'Clubs',
        table: 'clubs',
        orderBy: { column: 'club_name', ascending: true },
        columns: [
          { key: 'id', label: 'ID', mono: true },
          { key: 'sport_key', label: 'Sport' },
          { key: 'club_name', label: 'Club' },
          { key: 'country', label: 'Country' },
          { key: 'school_type', label: 'School type' },
          { key: 'external_ncaa_id', label: 'NCAA ID', mono: true },
        ],
      },
      {
        key: 'teams',
        label: 'Teams',
        table: 'teams',
        orderBy: { column: 'id', ascending: true },
        columns: [
          { key: 'id', label: 'ID', mono: true },
          { key: 'sport_key', label: 'Sport' },
          { key: 'club_id', label: 'Club', mono: true },
          { key: 'competition_id', label: 'Competition', mono: true },
          { key: 'conference_id', label: 'Conference', mono: true },
          { key: 'team_gender', label: 'Gender' },
        ],
      },
      {
        key: 'venues',
        label: 'Venues',
        table: 'venues',
        description: 'Arenas / venues where matches are played.',
        orderBy: { column: 'venue_name', ascending: true },
        columns: [
          { key: 'id', label: 'ID', mono: true },
          { key: 'sport_key', label: 'Sport' },
          { key: 'venue_name', label: 'Venue' },
          { key: 'city', label: 'City' },
          { key: 'state', label: 'State' },
          { key: 'country', label: 'Country' },
          { key: 'seating_capacity', label: 'Capacity', mono: true },
        ],
      },
      {
        key: 'competition_competitors',
        label: 'Competition competitors',
        table: 'competition_competitors',
        description: 'Team ↔ competition memberships — the real roster of teams entered in each competition.',
        orderBy: { column: 'id', ascending: true },
        columns: [
          { key: 'id', label: 'ID', mono: true },
          { key: 'sport_key', label: 'Sport' },
          { key: 'competition_id', label: 'Competition', mono: true },
          { key: 'team_id', label: 'Team', mono: true },
          { key: 'competitor_name', label: 'Competitor' },
          { key: 'conference_id', label: 'Conference', mono: true },
        ],
      },
      {
        key: 'persons',
        label: 'Persons',
        table: 'persons',
        // persons is fed by three person endpoints (the primary league feed,
        // competition rosters, and the league-team enrichment).
        harvestEndpointTypes: ['league-persons', 'competition-team-persons', 'league-team-persons'],
        orderBy: { column: 'last_name', ascending: true },
        columns: [
          { key: 'id', label: 'ID', mono: true },
          { key: 'sport_key', label: 'Sport' },
          { key: 'first_name', label: 'First' },
          { key: 'last_name', label: 'Last' },
          { key: 'gender', label: 'Gender' },
          { key: 'availability', label: 'Availability' },
        ],
      },
      {
        key: 'competition_persons',
        label: 'Competition persons',
        table: 'competition_persons',
        // Fed by the per-competition roster endpoint, not a `competition-persons` route.
        harvestEndpointTypes: ['competition-team-persons'],
        description: 'Per-competition roster entries (person ↔ team ↔ competition).',
        orderBy: { column: 'id', ascending: false },
        columns: [
          { key: 'id', label: 'ID', mono: true },
          { key: 'sport_key', label: 'Sport' },
          { key: 'competition_id', label: 'Competition', mono: true },
          { key: 'team_id', label: 'Team', mono: true },
          { key: 'person_id', label: 'Person', mono: true },
          { key: 'playing_position', label: 'Position' },
        ],
      },
      {
        key: 'team_persons',
        label: 'Team persons',
        table: 'team_persons',
        // Roster membership, enriched from the league- and competition-team person feeds.
        harvestEndpointTypes: ['league-team-persons', 'competition-team-persons'],
        description: 'Team ↔ person membership with first/last-seen tracking.',
        orderBy: { column: 'id', ascending: false },
        columns: [
          { key: 'id', label: 'ID', mono: true },
          { key: 'sport_key', label: 'Sport' },
          { key: 'team_id', label: 'Team', mono: true },
          { key: 'person_id', label: 'Person', mono: true },
          { key: 'last_seen_at', label: 'Last seen', format: isoDate },
          { key: 'is_ghost_only', label: 'Ghost only' },
        ],
      },
      {
        key: 'matches',
        label: 'Matches',
        table: 'matches',
        orderBy: { column: 'match_time_utc', ascending: false },
        columns: [
          { key: 'id', label: 'ID', mono: true },
          { key: 'sport_key', label: 'Sport' },
          { key: 'competition_id', label: 'Competition', mono: true },
          { key: 'home_team_id', label: 'Home', mono: true },
          { key: 'away_team_id', label: 'Away', mono: true },
          { key: 'match_time_utc', label: 'Scheduled', format: isoDate },
          { key: 'match_status', label: 'Status' },
        ],
      },
      {
        key: 'match_team_stats',
        label: 'Match team stats',
        table: 'match_team_stats',
        orderBy: { column: 'id', ascending: false },
        columns: [
          { key: 'id', label: 'ID', mono: true },
          { key: 'match_id', label: 'Match', mono: true },
          { key: 'team_id', label: 'Team', mono: true },
          { key: 'sync_watermark', label: 'Last sync', mono: true },
        ],
      },
      {
        key: 'match_person_stats',
        label: 'Match person stats',
        table: 'match_person_stats',
        orderBy: { column: 'id', ascending: false },
        columns: [
          { key: 'id', label: 'ID', mono: true },
          { key: 'match_id', label: 'Match', mono: true },
          { key: 'person_id', label: 'Person', mono: true },
          { key: 'team_id', label: 'Team', mono: true },
        ],
      },
      {
        key: 'team_competition_stats',
        label: 'Team competition stats',
        table: 'team_competition_stats',
        orderBy: { column: 'id', ascending: false },
        columns: [
          { key: 'id', label: 'ID', mono: true },
          { key: 'sport_key', label: 'Sport' },
          { key: 'competition_id', label: 'Competition', mono: true },
          { key: 'team_id', label: 'Team', mono: true },
          { key: 'games_played', label: 'GP', mono: true },
          { key: 'wins', label: 'W', mono: true },
          { key: 'losses', label: 'L', mono: true },
        ],
      },
      {
        key: 'person_competition_stats',
        label: 'Person competition stats',
        table: 'person_competition_stats',
        orderBy: { column: 'id', ascending: false },
        columns: [
          { key: 'id', label: 'ID', mono: true },
          { key: 'sport_key', label: 'Sport' },
          { key: 'competition_id', label: 'Competition', mono: true },
          { key: 'person_id', label: 'Person', mono: true },
          { key: 'team_id', label: 'Team', mono: true },
          { key: 'games_played', label: 'GP', mono: true },
          { key: 'minutes_played', label: 'MIN', mono: true },
        ],
      },
    ],
  },
  {
    key: 'presto',
    label: 'Presto Sports',
    schema: PRESTO_V2_SCHEMA,
    hangfireQueue: 'harvest-presto',
    sports: HARVEST_SPORTS,
    tagline: 'Multi-sport NCAA Presto gameday data. Backed by the presto_v2.* schema, fed by the throttled PrestoHarvestJob pipeline.',
    entities: [
      {
        key: 'sports',
        label: 'Sports',
        table: 'sports',
        orderBy: { column: 'id', ascending: true },
        columns: [
          { key: 'id', label: 'ID', mono: true },
          { key: 'vendor_id', label: 'Vendor ID', mono: true },
          { key: 'sport_key', label: 'Sport' },
          { key: 'display_name', label: 'Display name' },
        ],
      },
      {
        key: 'organizations',
        label: 'Organizations',
        table: 'organizations',
        orderBy: { column: 'organization_name', ascending: true },
        columns: [
          { key: 'id', label: 'ID', mono: true },
          { key: 'sport_key', label: 'Sport' },
          { key: 'organization_name', label: 'Name' },
          { key: 'short_name', label: 'Short' },
        ],
      },
      {
        key: 'seasons',
        label: 'Seasons',
        table: 'seasons',
        orderBy: { column: 'season_year', ascending: false },
        columns: [
          { key: 'id', label: 'ID', mono: true },
          { key: 'sport_key', label: 'Sport' },
          { key: 'season_year', label: 'Year' },
          { key: 'is_active', label: 'Active' },
        ],
      },
      {
        key: 'tournaments',
        label: 'Tournaments',
        table: 'tournaments',
        orderBy: { column: 'id', ascending: false },
        columns: [
          { key: 'id', label: 'ID', mono: true },
          { key: 'sport_key', label: 'Sport' },
          { key: 'tournament_name', label: 'Name' },
          { key: 'season_id', label: 'Season', mono: true },
        ],
      },
      {
        key: 'teams',
        label: 'Teams',
        table: 'teams',
        orderBy: { column: 'team_name', ascending: true },
        columns: [
          { key: 'id', label: 'ID', mono: true },
          { key: 'sport_key', label: 'Sport' },
          { key: 'team_name', label: 'Team' },
          { key: 'organization_id', label: 'Org', mono: true },
        ],
      },
      {
        key: 'players',
        label: 'Players',
        table: 'players',
        orderBy: { column: 'last_name', ascending: true },
        columns: [
          { key: 'id', label: 'ID', mono: true },
          { key: 'sport_key', label: 'Sport' },
          { key: 'first_name', label: 'First' },
          { key: 'last_name', label: 'Last' },
          { key: 'career_id', label: 'Career', mono: true },
        ],
      },
      {
        key: 'careers',
        label: 'Careers',
        table: 'careers',
        // Player identities are derived from the career-stats harvest payloads.
        harvestEndpointTypes: ['career-stats'],
        description: 'Season-spanning player identities (Presto careerId).',
        orderBy: { column: 'last_name', ascending: true },
        columns: [
          { key: 'id', label: 'ID', mono: true },
          { key: 'sport_key', label: 'Sport' },
          { key: 'first_name', label: 'First' },
          { key: 'last_name', label: 'Last' },
          { key: 'full_name', label: 'Name' },
          { key: 'hometown_state', label: 'State' },
        ],
      },
      {
        key: 'events',
        label: 'Events',
        table: 'events',
        orderBy: { column: 'start_date_time', ascending: false },
        columns: [
          { key: 'id', label: 'ID', mono: true },
          { key: 'sport_key', label: 'Sport' },
          { key: 'home_team_id', label: 'Home', mono: true },
          { key: 'away_team_id', label: 'Away', mono: true },
          { key: 'start_date_time', label: 'Scheduled', format: isoDate },
        ],
      },
      {
        key: 'event_rosters',
        label: 'Event rosters',
        table: 'event_rosters',
        description: 'Per-event lineups (event ↔ team ↔ player).',
        orderBy: { column: 'id', ascending: false },
        columns: [
          { key: 'id', label: 'ID', mono: true },
          { key: 'sport_key', label: 'Sport' },
          { key: 'event_id', label: 'Event', mono: true },
          { key: 'team_id', label: 'Team', mono: true },
          { key: 'player_id', label: 'Player', mono: true },
          { key: 'shirt_number', label: 'Shirt', mono: true },
          { key: 'starter', label: 'Starter' },
        ],
      },
      {
        key: 'event_team_stats',
        label: 'Event team stats',
        table: 'event_team_stats',
        orderBy: { column: 'id', ascending: false },
        columns: [
          { key: 'id', label: 'ID', mono: true },
          { key: 'event_id', label: 'Event', mono: true },
          { key: 'team_id', label: 'Team', mono: true },
        ],
      },
      {
        key: 'event_player_stats',
        label: 'Event player stats',
        table: 'event_player_stats',
        orderBy: { column: 'id', ascending: false },
        columns: [
          { key: 'id', label: 'ID', mono: true },
          { key: 'event_id', label: 'Event', mono: true },
          { key: 'player_id', label: 'Player', mono: true },
        ],
      },
      {
        key: 'team_season_stats',
        label: 'Team season stats',
        table: 'team_season_stats',
        orderBy: { column: 'id', ascending: false },
        columns: [
          { key: 'id', label: 'ID', mono: true },
          { key: 'sport_key', label: 'Sport' },
          { key: 'team_id', label: 'Team', mono: true },
          { key: 'season_id', label: 'Season', mono: true },
        ],
      },
      {
        key: 'career_stats',
        label: 'Career stats',
        table: 'career_stats',
        orderBy: { column: 'id', ascending: false },
        columns: [
          { key: 'id', label: 'ID', mono: true },
          { key: 'sport_key', label: 'Sport' },
          { key: 'career_id', label: 'Career', mono: true },
          { key: 'season_id', label: 'Season', mono: true },
        ],
      },
      {
        key: 'team_records',
        label: 'Team records',
        table: 'team_records',
        orderBy: { column: 'id', ascending: false },
        columns: [
          { key: 'id', label: 'ID', mono: true },
          { key: 'sport_key', label: 'Sport' },
          { key: 'team_id', label: 'Team', mono: true },
          { key: 'season_id', label: 'Season', mono: true },
          { key: 'wins', label: 'W', mono: true },
          { key: 'losses', label: 'L', mono: true },
          { key: 'ties', label: 'T', mono: true },
        ],
      },
      {
        key: 'rpi_data',
        label: 'RPI data',
        table: 'rpi_data',
        description: 'Ratings Percentage Index values and ranks per organization/season.',
        orderBy: { column: 'id', ascending: false },
        columns: [
          { key: 'id', label: 'ID', mono: true },
          { key: 'sport_key', label: 'Sport' },
          { key: 'organization_id', label: 'Org', mono: true },
          { key: 'season_id', label: 'Season', mono: true },
          { key: 'rpi_value', label: 'RPI', mono: true },
          { key: 'rank_overall', label: 'Rank', mono: true },
          { key: 'computed_at', label: 'Computed' },
        ],
      },
    ],
  },
];

export function findProvider(key: string): ProviderDefinition | undefined {
  return PROVIDERS.find((p) => p.key === key);
}

export function findEntity(provider: ProviderDefinition, key: string): EntityDefinition | undefined {
  return provider.entities.find((e) => e.key === key);
}

/**
 * harvest.manifest `endpoint_type`(s) that feed an entity, used to look up its
 * last-harvested timestamp and harvest-vs-sync series. Defaults to the table
 * name hyphenated (e.g. `event_team_stats` → `event-team-stats`).
 */
export function harvestEndpointTypes(entity: EntityDefinition): string[] {
  return entity.harvestEndpointTypes ?? [entity.table.replace(/_/g, '-')];
}

/** Union of all sport keys across all providers — used by global filters. */
export function allSports(): string[] {
  const set = new Set<string>();
  for (const p of PROVIDERS) for (const s of p.sports) set.add(s);
  return [...set].sort();
}
